<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { Nullable } from '@nova/core';

import NextStep from '@/components/NextStep.vue';
import { CheckoutFlowStep, useCheckoutFlow } from '@/composables/useCheckoutFlow';
import { useWarehouseInfo } from '@/composables/useWarehouseInfo';
import useNotify from '@/composables/useNotify';
import { useDialogTrigger } from '@/composables/useEventBasedDialog';
import checkoutService from '@/services/checkoutService';
import { useCheckoutAnalytics } from '@/composables/useCheckoutAnalytics';
import { useRouter } from 'vue-router';

const router = useRouter();
const checkoutFlow = useCheckoutFlow();
const { warehouseData } = useWarehouseInfo();

const { trackViewBolPageCompleted } = useCheckoutAnalytics();
const { notify } = useNotify();
const { t } = useI18n();
const checkoutApi = checkoutService();

const { triggerDialog: triggerCompleteCheckoutDialog } = useDialogTrigger(
  'open-complete-checkout-dialog'
);

const isDocumentLoading = ref(false);
const bolDocumentString = ref<Nullable<string>>(null);

const isFormValid = ref(false);
const driverName = ref('');
const eSignatureAgreement = ref(false);

const rules = {
  driverName: (value: string) => {
    if (!value) return t('viewBolPage.validation.driverNameRequired');
    return true;
  },
  eSignatureAgreement: (value: boolean) => {
    if (!value) return t('viewBolPage.validation.eSignatureRequired');
    return true;
  }
};

const fetchBolDocument = async () => {
  isDocumentLoading.value = true;
  try {
    const bolData = await checkoutApi.getBol(
      warehouseData.value.id,
      checkoutFlow.appointmentId.value
    );
    const bolPdf = await checkoutApi.previewBolPdf(warehouseData.value.id, bolData.originalDoc.id);
    const blob = new Blob([bolPdf], { type: 'application/pdf' });
    bolDocumentString.value = URL.createObjectURL(blob) + '#toolbar=0&navpanes=0&scrollbar=0';
  } catch (error) {
    checkoutFlow.setStepValid(CheckoutFlowStep.ViewBolPage, false);
    console.error('Error fetching BOL document:', error);
    router.push({ name: 'bol-error' });
  } finally {
    isDocumentLoading.value = false;
  }
};

onMounted(async () => {
  await fetchBolDocument();
});

watch(isFormValid, async newVal => {
  const canSubmit = newVal && bolDocumentString.value !== null;
  checkoutFlow.setStepValid(CheckoutFlowStep.ViewBolPage, canSubmit);
});

const handleSubmit = async () => {
  checkoutFlow.setIsStepLoading(true);
  try {
    await checkoutApi.signBol(warehouseData.value.id, {
      appointmentId: checkoutFlow.appointmentId.value,
      driverFullName: driverName.value
    });

    const pendingPayload = checkoutFlow.pendingCustomFormsPayload.value;
    const customFormsData =
      Array.isArray(pendingPayload) && pendingPayload.length > 0 ? pendingPayload : undefined;
    checkoutFlow.setPendingCustomFormsPayload(null);

    trackViewBolPageCompleted();

    triggerCompleteCheckoutDialog({
      appointmentId: checkoutFlow.appointmentId.value,
      assetVisitId: checkoutFlow.assetVisitId.value,
      customFormsData
    });
  } catch (error) {
    let errorMessage = t('viewBolPage.errors.signFailed');

    if (typeof error?.response?.data?.message === 'string') {
      errorMessage = error.response.data.message;
    }

    notify(errorMessage, 'error');

    console.error('Error submitting e-signature:', error);
  } finally {
    checkoutFlow.setIsStepLoading(false);
  }
};
</script>

<template>
  <div class="juno-i18n-exclude d-flex flex-column gap-6 px-4 fill-height">
    <v-btn
      variant="text"
      prepend-icon="mdi-arrow-left"
      style="align-self: flex-start"
      @click="checkoutFlow.setPreviousStep(CheckoutFlowStep.ValidationPage)">
      {{ t('common.actions.back') }}
    </v-btn>

    <h3 class="heading-md-bold">{{ t('viewBolPage.heading') }}</h3>

    <div style="width: 100%">
      <div
        v-if="isDocumentLoading"
        class="d-flex flex-column align-center justify-center gap-4 py-16 my-auto">
        <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
        <p class="text-body-1 text-grey">{{ t('viewBolPage.loadingMessage') }}</p>
      </div>
      <iframe
        v-else
        :src="bolDocumentString"
        style="height: 400px; width: 100%; border: none"
        :aria-label="t('viewBolPage.iframeAriaLabel')"></iframe>
    </div>

    <v-form ref="form" @submit.prevent="handleSubmit" v-model="isFormValid" fast-fail>
      <div class="checkout-form-body d-flex flex-column gap-3">
        <div class="d-flex flex-column gap-2 form-field">
          <v-label class="typography-body-sm-bold required-field">
            {{ t('viewBolPage.driverNameLabel') }}
          </v-label>
          <v-text-field
            v-model="driverName"
            variant="outlined"
            density="default"
            :placeholder="t('viewBolPage.driverNamePlaceholder')"
            :rules="[rules.driverName]"
            validate-on="input"></v-text-field>
        </div>

        <div class="d-flex flex-column form-field">
          <v-checkbox
            v-model="eSignatureAgreement"
            :rules="[rules.eSignatureAgreement]"
            density="compact"
            :label="t('viewBolPage.eSignatureAgreementLabel')"
            class="typography-body-sm"></v-checkbox>
        </div>
      </div>
    </v-form>
  </div>

  <next-step
    :is-step-valid="checkoutFlow.isStepValid(checkoutFlow.step.value)"
    :on-step-complete="handleSubmit" />
</template>

<style scoped lang="scss">
.checkout-form-body .form-field {
  .v-input {
    margin-top: 0;
  }

  .v-label {
    position: relative;
    color: $color-text-secondary;
    align-self: flex-start;
    opacity: 1;
  }
}
</style>
